// deriveVersionBarModel — derives the version bar's session-scoped pip range,
// live marker, provenance, and failed pseudo-pip (colony-screen design D6;
// client-colony "Version bar with session-scoped history and one-tap
// rollback"). Pure derivation over the colony slice: no store mutation, no
// dispatch, no rendering — `VersionRow`/`Colony.tsx` (task groups 7/8) render
// what this module derives, and their **Activate** action dispatches
// `Rollback{targetVersion: pip.version}` directly (this module has no opinion
// on dispatch).
//
// The corrected numbering rule (design D6): the wire's
// `versionHeads[type].headVersion` is a **duplicate projection of**
// `activeVersion` (both read the one server-side CAS head) — never a count or
// a range maximum, and it moves **backward** on rollback. So the pip range is
// `1..max(activeVersion, highest version observed live this session)`, where
// "observed live this session" is read off the session-accreted
// `versionCache`/`provenanceLog` for this type (never off `versionHeads`).

import type { ClientStoreState, VersionProvenance } from '../../state/store';
import { versionCacheKey } from '../../state/store';

/** One version-bar pip. `hasSource` mirrors whether `versionCache` holds this
 * version's canonical text (a snapshot body can render); `provenance` is the
 * session-observed `PromptStatus` history for it, or `null` for a version in
 * range that was never seen live this session (created before connect — the
 * bare-`vN` case, `D-072`). */
export interface VersionPip {
  version: number;
  isLive: boolean;
  hasSource: boolean;
  provenance: VersionProvenance | null;
}

/** A failed generation observed for the type: a red diagnostics pseudo-pip,
 * never an activatable version (it carries no `version` number) and never a
 * cooldown charge (it carries no cooldown field at all — the type simply has
 * none to show). */
export interface FailedPip {
  tick: number;
  diagnostics: string[];
}

export interface VersionBarModel {
  typeId: number;
  /** `ColonyMeta.types[i].activeVersion` for this type — the live pip. */
  liveVersion: number;
  /** Ascending `1..max(activeVersion, highest observed)`, per design D6. */
  pips: VersionPip[];
  /** The latest `PromptStatus.failed` observed for this type, or `null` if the
   * latest observed status for it is not a failure (including "no status
   * observed yet" and "superseded by a later generating/accepted"). */
  failedPip: FailedPip | null;
}

type VersionBarSlice = Pick<
  ClientStoreState,
  'colonyMeta' | 'versionCache' | 'provenanceLog' | 'promptStatusByType'
>;

/**
 * Derives the version bar model for `typeId` from the colony slice. Returns
 * `null` when `colonyMeta` hasn't arrived yet, or names no such type — the
 * sheet has nothing to render before that.
 */
export function deriveVersionBarModel(state: VersionBarSlice, typeId: number): VersionBarModel | null {
  const typeMeta = state.colonyMeta?.types.find((t) => t.typeId === typeId);
  if (typeMeta === undefined) return null;

  const activeVersion = typeMeta.activeVersion;
  const highestObserved = observedVersions(state, typeId).reduce(
    (max, v) => Math.max(max, v),
    activeVersion,
  );

  const pips: VersionPip[] = [];
  for (let version = 1; version <= highestObserved; version++) {
    const key = versionCacheKey(typeId, version);
    pips.push({
      version,
      isLive: version === activeVersion,
      hasSource: Object.hasOwn(state.versionCache, key),
      provenance: state.provenanceLog[key] ?? null,
    });
  }

  const promptStatus = state.promptStatusByType[typeId];
  const failedPip: FailedPip | null =
    promptStatus !== undefined && promptStatus.status.kind === 'failed'
      ? { tick: promptStatus.tick, diagnostics: promptStatus.status.diagnostics }
      : null;

  return { typeId, liveVersion: activeVersion, pips, failedPip };
}

/** Every version number of `typeId` accreted this session into either
 * `versionCache` (went live via a `ColonyMeta`) or `provenanceLog` (an
 * `accepted{v}` was observed) — the "observed live this session" signal design
 * D6 defines the pip-range maximum from. Both are keyed by
 * `versionCacheKey(typeId, version)`; a version normally lands in both, but
 * either alone is sufficient (e.g. an `accepted{v}` can be observed a tick
 * before its corresponding `ColonyMeta`). */
function observedVersions(
  state: Pick<ClientStoreState, 'versionCache' | 'provenanceLog'>,
  typeId: number,
): number[] {
  const prefix = `${typeId}:`;
  const versions: number[] = [];
  for (const key of Object.keys(state.versionCache)) {
    if (key.startsWith(prefix)) versions.push(Number(key.slice(prefix.length)));
  }
  for (const key of Object.keys(state.provenanceLog)) {
    if (key.startsWith(prefix)) versions.push(Number(key.slice(prefix.length)));
  }
  return versions.filter((v) => Number.isFinite(v));
}

const SECONDS_PER_MINUTE = 60;

/**
 * Formats a pip's session provenance as a one-line summary (design D6:
 * "prompted-ago ... auto-repaired ×N"): `nowTick` is the caller's best-known
 * current tick (e.g. the latest `matchState`/`colonyMeta` tick observed); the
 * result is advisory display text only — a `Rejected{rateLimited}` remains the
 * authoritative cooldown signal (D7), this formatter makes no cooldown claim.
 *
 * `tickRateHz` is **wire-required** (client-tick-rate-provenance, tick-rate-single-source
 * E4): sourced from `Welcome.manifest.tickRateHz` by every caller — no defaulted argument.
 */
export function formatProvenanceSummary(
  provenance: VersionProvenance,
  nowTick: number,
  tickRateHz: number,
): string {
  const elapsedSeconds = Math.max(0, Math.round((nowTick - provenance.acceptedTick) / tickRateHz));
  const agoText =
    elapsedSeconds < SECONDS_PER_MINUTE
      ? `${elapsedSeconds}s ago`
      : `${Math.floor(elapsedSeconds / SECONDS_PER_MINUTE)}m ago`;
  const repairText = provenance.repairCount > 0 ? ` · auto-repaired ×${provenance.repairCount}` : '';
  return `Prompted ${agoText}${repairText}`;
}
